import { WORLD, Rect, District } from './data';
import { roadTiles } from './country';

export const TILE = 16;
/** RAIL: the fenced track bed of the station line. Solid for people and cars, but you can see and shoot across it. */
export enum T { BUILDING = 0, ROAD = 1, SIDEWALK = 2, ALLEY = 3, PLAZA = 4, LOW = 5, THIN = 6, STALL = 7, BOLLARD = 8, RAIL = 9 }
/** One letter per tile type, indexed by T (tile dumps, fixtures and previews). */
export const TILE_LETTERS = 'BRSAPLTKOX';

/** Is tile (tx, ty) inside a tile rect? */
export const inTileRect = (tx: number, ty: number, r: Rect) => tx >= r.x && ty >= r.y && tx < r.x + r.w && ty < r.y + r.h;
/** The district (world.json `districts`) a tile belongs to. Districts tile the whole map without overlap. */
export function districtAt(tx: number, ty: number): District {
  return WORLD.districts.find(d => inTileRect(tx, ty, d)) ?? WORLD.districts[0];
}

export interface Intersection { id: number; cx: number; cy: number; x0: number; y0: number; x1: number; y1: number; hIdx: number; vIdx: number }
export interface GameMap {
  w: number; h: number; tiles: Uint8Array;
  intersections: Intersection[];
  hRoads: number[]; vRoads: number[]; roadW: number;
  xMin: number; xMax: number; yMin: number; yMax: number; // road extents in px
  /** the sanctuary's open countryside (grass and forest floor): bollard tiles that cars and bikes may cross (sim.ts boxHits);
   *  every other bollard keeps cars out. Index y * w + x. */
  grass?: Uint8Array;
}

export function buildMap(): GameMap {
  const w = WORLD.width, h = WORLD.height;
  const tiles = new Uint8Array(w * h); // BUILDING
  const set = (x: number, y: number, t: T) => { if (x >= 0 && y >= 0 && x < w && y < h) tiles[y * w + x] = t; };
  const fill = (r: Rect, t: T) => { for (let y = r.y; y < r.y + r.h; y++) for (let x = r.x; x < r.x + r.w; x++) set(x, y, t); };
  const { h: hr, v: vr, width: rw, sidewalk: sw } = WORLD.roads;
  const x0 = vr[0], x1 = vr[vr.length - 1] + rw - 1, y0 = hr[0], y1 = hr[hr.length - 1] + rw - 1;
  for (const ry of hr) fill({ x: x0, y: ry, w: x1 - x0 + 1, h: rw }, T.ROAD);
  for (const rx of vr) fill({ x: rx, y: y0, w: rw, h: y1 - y0 + 1 }, T.ROAD);
  // sidewalks: building tiles within the district's sidewalk width of a road (Nishi-Shinjuku has broader ones)
  const isRoad = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && tiles[y * w + x] === T.ROAD;
  const side: number[] = [];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (tiles[y * w + x] !== T.BUILDING) continue;
    const s = districtAt(x, y).sidewalk ?? sw;
    let near = false;
    for (let dy = -s; dy <= s && !near; dy++) for (let dx = -s; dx <= s; dx++) if (isRoad(x + dx, y + dy)) { near = true; break; }
    if (near) side.push(y * w + x);
  }
  for (const i of side) tiles[i] = T.SIDEWALK;
  // the track bed replaces only building ground: roads and their sidewalks cross it as level crossings
  for (const r of WORLD.rails) for (let y = r.y; y < r.y + r.h; y++) for (let x = r.x; x < r.x + r.w; x++) if (tiles[y * w + x] === T.BUILDING) tiles[y * w + x] = T.RAIL;
  // the sanctuary countryside: walkable forest floor (bollard ground: people pass, cars don't), its solid parts cut back out
  const S = WORLD.sanctuary;
  for (const f of S?.forest ?? []) fill(f, T.BOLLARD);
  for (const s of S?.solid ?? []) fill(s, T.BUILDING);
  if (S?.road) for (const i of roadTiles(S.road, w, h)) tiles[i] = T.ALLEY; // the winding mountain road (country.ts)
  for (const p of WORLD.plazas) fill(p, T.PLAZA);
  for (const a of WORLD.alleys) {
    fill(a, T.ALLEY);
    if (a.kind === 'foot') {
      if (a.w > a.h) { fill({ x: a.x, y: a.y, w: 1, h: a.h }, T.BOLLARD); fill({ x: a.x + a.w - 1, y: a.y, w: 1, h: a.h }, T.BOLLARD); }
      else { fill({ x: a.x, y: a.y, w: a.w, h: 1 }, T.BOLLARD); fill({ x: a.x, y: a.y + a.h - 1, w: a.w, h: 1 }, T.BOLLARD); }
    }
  }
  for (const s of WORLD.stalls) fill(s, T.STALL);
  for (const l of WORLD.lows) fill(l, T.LOW);
  for (const t of WORLD.thins) fill(t, T.THIN);
  for (const b of WORLD.bollards) fill(b, T.BOLLARD); // keep cars out of pedestrian concourses
  for (const p of WORLD.pois) { const f = boothFootprint(p); if (f) fill(f, T.THIN); } // free-standing booths are solid
  let grass: Uint8Array | undefined;
  if (S?.forest?.length) { // the forest floor left after everything else was laid over it
    grass = new Uint8Array(w * h);
    for (const f of S.forest) for (let y = f.y; y < f.y + f.h; y++) for (let x = f.x; x < f.x + f.w; x++) if (x >= 0 && y >= 0 && x < w && y < h && tiles[y * w + x] === T.BOLLARD) grass[y * w + x] = 1;
  }
  const intersections: Intersection[] = [];
  hr.forEach((ry, hi) => vr.forEach((rx, vi) => {
    intersections.push({ id: intersections.length, x0: rx * TILE, y0: ry * TILE, x1: (rx + rw) * TILE, y1: (ry + rw) * TILE, cx: (rx + rw / 2) * TILE, cy: (ry + rw / 2) * TILE, hIdx: hi, vIdx: vi });
  }));
  return { w, h, tiles, intersections, hRoads: hr, vRoads: vr, roadW: rw, xMin: x0 * TILE, xMax: (x1 + 1) * TILE, yMin: y0 * TILE, yMax: (y1 + 1) * TILE, grass };
}

/** Solid footprint of a free-standing booth drawn at a point of interest, just north of its point (where players stand
 *  to use the counter): the koban 2x2, Hachi's stall 6 wide x 2 deep with a sidewalk row before its counter. Collision
 *  (buildMap) and the 2x art (hd-scene.ts, hd-place-shops.ts) both place the booth from this, so they can't drift. */
export function boothFootprint(p: { kind: string; x: number; y: number }): Rect | null {
  const x = Math.floor(p.x), y = Math.floor(p.y);
  if (p.kind === 'koban') return { x: x - 1, y: y - 2, w: 2, h: 2 };
  if (p.kind === 'weapons') return { x: x - 3, y: y - 3, w: 6, h: 2 };
  return null;
}
/** where a booth's art stands: bottom centre of its footprint, in world px */
export const boothFeet = (f: Rect) => ({ x: (f.x + f.w / 2) * TILE, y: (f.y + f.h) * TILE });

export function tileAt(m: GameMap, tx: number, ty: number): T {
  if (tx < 0 || ty < 0 || tx >= m.w || ty >= m.h) return T.BUILDING;
  return m.tiles[ty * m.w + tx] as T;
}
export const tileAtPx = (m: GameMap, px: number, py: number) => tileAt(m, Math.floor(px / TILE), Math.floor(py / TILE));
export const solidFoot = (t: T) => t === T.BUILDING || t === T.LOW || t === T.THIN || t === T.STALL || t === T.RAIL;
export const solidCar = (t: T) => solidFoot(t) || t === T.BOLLARD;
export const solidDrone = (t: T) => false && t;
export const blocksShot = (t: T) => t === T.BUILDING || t === T.THIN || t === T.STALL;
export const blocksSight = (t: T) => t === T.BUILDING || t === T.STALL;

/** DDA-ish ray march: returns true if clear. pierceThin lets rays pass thin cover. */
export function lineClear(m: GameMap, ax: number, ay: number, bx: number, by: number, sight = false, pierceThin = false): boolean {
  const d = Math.hypot(bx - ax, by - ay);
  const steps = Math.ceil(d / 6);
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    const tt = tileAtPx(m, ax + (bx - ax) * t, ay + (by - ay) * t);
    if (sight ? blocksSight(tt) : blocksShot(tt) && !(pierceThin && tt === T.THIN)) return false;
  }
  return true;
}

export function inRect(px: number, py: number, r: Rect) {
  return px >= r.x * TILE && py >= r.y * TILE && px < (r.x + r.w) * TILE && py < (r.y + r.h) * TILE;
}

/** Is a point (world px) inside a heal zone? A zone with a `circle` is that disc (tiles), otherwise its rect. */
export function inHealZone(px: number, py: number, z: Rect & { circle?: { cx: number; cy: number; r: number } }) {
  if (!inRect(px, py, z)) return false;
  const c = z.circle; return !c || Math.hypot(px - c.cx * TILE, py - c.cy * TILE) < c.r * TILE;
}

/** Road lane helpers (left-hand traffic). dir: 0=E,1=S,2=W,3=N */
export const DIRS = [[1, 0], [0, 1], [-1, 0], [0, -1]];
export function hLaneY(m: GameMap, hi: number, dir: number) { const c = (m.hRoads[hi] + m.roadW / 2) * TILE; return dir === 0 ? c - 16 : c + 16; }
export function vLaneX(m: GameMap, vi: number, dir: number) { const c = (m.vRoads[vi] + m.roadW / 2) * TILE; return dir === 1 ? c + 16 : c - 16; }
